import { forwardRef, useLayoutEffect, useEffect, useMemo, useRef, useState } from 'react';
import { ProseMirror } from 'use-prosemirror';
import { DataFetchingClient, DataFetchingClientProvider } from 'data-fetching-client';
import ProsemirrorMenu, { ProsemirrorFormattingTray } from './ProsemirrorMenu';
import { getDefaultEditorStyle } from './utils/fontUtils';
import { FocusPlugin } from './plugins/FocusPlugin';
import { useReactNodeViewPortals } from './utils/ReactNodeViewPortals';
import { buildNodeViewsFromConfig } from './utils/buildNodeViewsFromConfig';
import { viewConfig as defaultViewConfig } from './views/viewConfig';
import styled from 'styled-components';
import { FILE_DROP, INLINE_IMAGE, PASTE_OVERRIDE, PAYMENT_LINK, TRACKING } from './editorCapabilities';
import { usePerformanceMonitoring, useEditorUsageLogging } from '../utils/InputPerformanceMeasuring';
import { DropPlugin } from './plugins/DropPlugin';
import { buildDefaultRTEConfig, capabilitiesThatArePlugins } from './utils/configUtils';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import { AuthContext } from './contexts/AuthContext';
import { PaymentLinksEligibilityContext } from './contexts/PaymentLinksEligibilityContext';
import { PasteOverridePlugin } from './plugins/PasteOverridePlugin';
import { PaymentsEligibilityStatus, eligibility as fetchPaymentsEligibility } from './commands/paymentLink/PaymentLinksApi';
import { Tracker } from '../utils/usageTracker';
import { HSClipboardParser } from './utils/clipboardParser';
import { getGlobalVars } from './utils/globalVarUtil';
import usePrevious from 'react-utils/hooks/usePrevious';
import { useDndSetup } from './hooks/useDndSetup';
import { flushSync } from 'react-dom';
import { useTheme } from 'foundations-theming/utils';
import { useEditorUIContext } from './contexts/EditorUIContext';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const EditorBodyContainer = styled.div.withConfig({
  displayName: "Prosemirror__EditorBodyContainer"
})(["flex-grow:1;overflow:auto;display:flex;flex-direction:column;.ProseMirror,.ProsemirrorEditor__body{height:100%;&:focus{outline:none !important;}}"]);
const DropTargetConnector = /*#__PURE__*/forwardRef(({
  connectDropTarget,
  mainEditor
}, ref) => {
  const styledMainEditor = /*#__PURE__*/_jsx("div", {
    style: {
      flexGrow: 1
    },
    children: mainEditor
  });
  return /*#__PURE__*/_jsx(EditorBodyContainer, {
    ref: ref,
    "data-editor-body-container": "true",
    children: connectDropTarget ? connectDropTarget(styledMainEditor) : styledMainEditor
  });
});
const Prosemirror = /*#__PURE__*/forwardRef(({
  editorConfig,
  editorState,
  inputClassName,
  menuClassName,
  ariaLabel,
  ariaLabelledBy,
  setEditorState,
  customPlugins,
  dataTestId,
  performanceTrackingConfig,
  autoFocus = false,
  attachmentTray,
  submitButton,
  auth,
  disabledConfig,
  DropTargetWrapper,
  DragDropContext,
  usageTrackerProperties,
  viewConfig: customViewConfig
}, ref) => {
  const editorBodyRef = useRef(null);
  const usageTrackerPropertiesRef = useRef(usageTrackerProperties);
  const [paymentLinksEligibility, setPaymentLinksEligibility] = useState(PaymentsEligibilityStatus.INELIGIBLE);
  usePerformanceMonitoring({
    performanceTrackingConfig,
    ref: editorBodyRef
  });
  useEditorUsageLogging();
  const reactPortalApi = useReactNodeViewPortals();
  const nodeViews = useMemo(() => {
    return buildNodeViewsFromConfig( // Override the default view config with the custom view config
    Object.assign({}, defaultViewConfig, customViewConfig), reactPortalApi.createPortal, editorConfig);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customViewConfig, reactPortalApi.createPortal]);
  const editorStyles = useMemo(() => getDefaultEditorStyle(editorConfig), [editorConfig]);
  const dfcClient = useMemo(() => new DataFetchingClient(), []);
  const prevEditorConfig = usePrevious(editorConfig);
  useLayoutEffect(() => {
    const currentCapabilityCount = capabilitiesThatArePlugins.filter(val => editorConfig[val]).length;
    // On first mount (no prevEditorConfig), reconfigure if any capability plugins
    // are enabled so they get registered even when the initial EditorState was
    // created without them. This may cause one extra synchronous reconfigure on
    // mount, which is acceptable for correctness.
    const hasNewCapabilityPlugins = prevEditorConfig ? capabilitiesThatArePlugins.filter(val => prevEditorConfig[val]).length !== currentCapabilityCount : currentCapabilityCount > 0;
    if (hasNewCapabilityPlugins || customPlugins && customPlugins.some(p => !editorState.plugins.find(ep => ep.spec.key === p.spec.key))) {
      setEditorState(editorState.reconfigure(buildDefaultRTEConfig(customPlugins, editorConfig, undefined, reactPortalApi)));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customPlugins, editorState, editorConfig, prevEditorConfig]);

  // auto focus the editor when it is opened if the autofocus prop is set
  // empty dependencies array to only get the use effect to fire once
  useEffect(() => {
    if (autoFocus) {
      const tr = editorState.tr.setMeta(FocusPlugin, {
        focusView: true
      });
      setEditorState(editorState.apply(tr));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // empty dependencies array to only get the use effect to fire once
  useEffect(() => {
    // get the users payment links eligibility status
    // used in InsertMenu.tsx
    if (editorConfig[PAYMENT_LINK]) {
      fetchPaymentsEligibility().then(response => {
        setPaymentLinksEligibility(response.status);
      }).catch(() => {
        console.warn('Failed to fetch users paymentLinksEligibilityStatus');
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    const fileDrop = editorConfig[FILE_DROP];
    const usePlaintextMode = getGlobalVars(editorConfig).usePlaintextMode;
    if (fileDrop && fileDrop.fileDropConfig) {
      setEditorState(editorState.apply(editorState.tr.setMeta(DropPlugin, {
        fileDropConfig: Object.assign({}, fileDrop.fileDropConfig, {
          usePlaintextMode: usePlaintextMode || !editorConfig[INLINE_IMAGE]
        }),
        files: []
      })));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editorState.plugins]);
  useEffect(() => {
    const pasteOverrideConfig = editorConfig[PASTE_OVERRIDE];
    const globalVars = getGlobalVars(editorConfig);
    const {
      usePlaintextMode,
      plaintextOptions
    } = globalVars;
    if (pasteOverrideConfig) {
      setEditorState(editorState.apply(editorState.tr.setMeta(PasteOverridePlugin, {
        pasteOverrideConfig: Object.assign({}, pasteOverrideConfig, {
          usePlaintextMode,
          plaintextOptions
        })
      })));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editorState.plugins]);
  useEffect(() => {
    if (usageTrackerPropertiesRef.current) {
      Tracker.setProperties(usageTrackerPropertiesRef.current);
    }
  }, []);
  const safePasteHandler = editorConfig[TRACKING] ? editorConfig[TRACKING].onPaste : undefined;
  const clipboardParser = useMemo(() => HSClipboardParser.fromSchema(editorState.schema), [editorState.schema]);
  const onChange = newState => {
    // flushSync needed for createRoot()-enabled apps
    flushSync(() => {
      setEditorState(newState);
    });
  };
  const mainEditor =
  /*#__PURE__*/
  // @bend-expect trellis-tokenized
  _jsx(ProseMirror, {
    className: "ProsemirrorEditor__body",
    state: editorState,
    onChange: onChange,
    nodeViews: nodeViews,
    editable: () => !(disabledConfig && disabledConfig.isDisabled),
    attributes: Object.assign({
      style: editorStyles,
      'data-test-id': dataTestId || 'rte-content'
    }, ariaLabel !== undefined && {
      'aria-label': ariaLabel
    }, ariaLabelledBy !== undefined && {
      'aria-labelledby': ariaLabelledBy
    }, {
      dir: 'auto'
    }),
    clipboardParser: clipboardParser,
    clipboardTextParser: clipboardParser.parseClipboardText,
    handlePaste: safePasteHandler,
    ref: ref
  });
  const EditorBodyContainerWrapper = useMemo(() => {
    return DropTargetWrapper && DragDropContext ? DragDropContext()(DropTargetWrapper(DropTargetConnector)) : DropTargetConnector;
  }, [DropTargetWrapper, DragDropContext]);
  const containerRef = useDndSetup(editorState, setEditorState, editorConfig);
  const trellisTheme = useTheme();
  const {
    showFormattingTray
  } = useEditorUIContext();
  const finalContainer =
  /*#__PURE__*/
  // @bend-expect trellis-tokenized
  _jsxs("div", {
    className: `ProsemirrorEditor__wrapper ${inputClassName}`,
    style: {
      paddingTop: trellisTheme.space['300'],
      paddingLeft: trellisTheme.space['500'],
      paddingRight: trellisTheme.space['500'],
      paddingBottom: trellisTheme.space['100']
    },
    children: [/*#__PURE__*/_jsx(EditorBodyContainerWrapper, {
      ref: containerRef,
      editorState: editorState,
      setEditorState: setEditorState,
      editorConfig: editorConfig,
      mainEditor: mainEditor
    }), attachmentTray && attachmentTray({}), /*#__PURE__*/_jsx(AuthContext.Provider, {
      value: auth,
      children: /*#__PURE__*/_jsx(PaymentLinksEligibilityContext.Provider, {
        value: paymentLinksEligibility,
        children: /*#__PURE__*/_jsx(DataFetchingClientProvider, {
          client: dfcClient,
          children: /*#__PURE__*/_jsxs("div", {
            style: disabledConfig && disabledConfig.isDisabled ? {
              pointerEvents: 'none'
            } : {},
            children: [showFormattingTray && /*#__PURE__*/_jsx(ProsemirrorFormattingTray, {
              editorConfig: editorConfig
            }), /*#__PURE__*/_jsx(ProsemirrorMenu, {
              editorConfig: editorConfig,
              submitButton: submitButton,
              menuClassName: menuClassName
            })]
          })
        })
      })
    })]
  });
  if (disabledConfig && disabledConfig.isDisabled) {
    return /*#__PURE__*/_jsx(Tooltip, {
      title: disabledConfig.disabledText,
      children: finalContainer
    });
  }
  return finalContainer;
});
export { Prosemirror };